/**
 * Lays a SQL query out on several lines for reading: each clause on its own line, a multi-column
 * select list one column per line, and subqueries indented under their parentheses.
 *
 * It only ever changes whitespace between tokens, and never the tokens themselves, so the query
 * means exactly what it meant. Whatever it is not sure about is returned unchanged: comments
 * (moving a line break can end a `--` comment early), dollar quoting, an unterminated string or
 * unbalanced parentheses. Formatting its own output gives the same output.
 *
 * Inside a function call or a window (`ROW_NUMBER() OVER (ORDER BY ...)`) nothing is broken.
 */

type Token = { text: string; spaceBefore: boolean };

const INDENT = '  ';

// Words that start a clause of the query they sit in.
const CLAUSES = new Set([
  'SELECT',
  'FROM',
  'WHERE',
  'HAVING',
  'LIMIT',
  'OFFSET',
  'QUALIFY',
  'WITH',
  'UNION',
  'INTERSECT',
  'EXCEPT',
  'JOIN',
]);
// Clauses spelt with two words, broken before the first.
const TWO_WORD_CLAUSES = new Set(['GROUP', 'ORDER']);
const JOIN_PREFIXES = new Set(['LEFT', 'RIGHT', 'FULL', 'INNER', 'CROSS', 'NATURAL', 'OUTER']);

/** Splits into tokens, remembering whether whitespace preceded each. Null when unsure. */
function tokenize(sql: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  let space = false;
  while (i < sql.length) {
    const c = sql[i];
    if (/\s/.test(c)) {
      space = true;
      i += 1;
      continue;
    }
    if ((c === '-' && sql[i + 1] === '-') || (c === '/' && sql[i + 1] === '*')) return null;
    if (c === '$' && /[$A-Za-z_]/.test(sql[i + 1] || '')) return null;
    let end = i + 1;
    if (c === "'" || c === '"' || c === '`') {
      // A doubled quote is an escaped one, inside the same literal.
      for (;;) {
        if (end >= sql.length) return null;
        if (sql[end] === c) {
          if (sql[end + 1] === c) end += 2;
          else break;
        } else end += 1;
      }
      end += 1;
    } else if (/[A-Za-z0-9_.]/.test(c)) {
      while (end < sql.length && /[A-Za-z0-9_.]/.test(sql[end])) end += 1;
    }
    tokens.push({ text: sql.slice(i, end), spaceBefore: space });
    space = false;
    i = end;
  }
  return tokens;
}

const word = (t: Token | undefined) => (t ? t.text.toUpperCase() : '');

export function formatSql(sql: string): string {
  const parsed = tokenize(sql);
  if (!parsed || !parsed.length) return sql;
  const tokens: Token[] = parsed;

  // For each '(', whether it opens a subquery; for each ')', the '(' it closes.
  const opener = new Map<number, number>();
  const subquery = new Set<number>();
  const stack: number[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i].text === '(') {
      stack.push(i);
      const next = word(tokens[i + 1]);
      if (next === 'SELECT' || next === 'WITH') subquery.add(i);
    } else if (tokens[i].text === ')') {
      const open = stack.pop();
      if (open === undefined) return sql;
      opener.set(i, open);
    }
  }
  if (stack.length) return sql;

  // Whether the select list starting after token `start` has a comma of its own (not one inside a
  // call or subquery), which is what makes it worth one column per line.
  const listHasComma = (start: number) => {
    let depth = 0;
    for (let i = start + 1; i < tokens.length; i += 1) {
      const t = tokens[i].text;
      if (t === '(') depth += 1;
      else if (t === ')') {
        if (depth === 0) return false;
        depth -= 1;
      } else if (depth === 0) {
        if (t === ',') return true;
        if (isClauseAt(i)) return false;
      }
    }
    return false;
  };

  // Whether token `i` starts a clause (given it sits at query level, not inside a call).
  function isClauseAt(i: number): boolean {
    const w = word(tokens[i]);
    if (w === 'JOIN') return !JOIN_PREFIXES.has(word(tokens[i - 1]));
    // Only where a query starts: `TIMESTAMP WITH TIME ZONE` is a type, not a clause.
    if (w === 'WITH') return i === 0 || ['(', ';'].includes(tokens[i - 1].text);
    if (CLAUSES.has(w)) return true;
    if (TWO_WORD_CLAUSES.has(w)) return word(tokens[i + 1]) === 'BY';
    if (JOIN_PREFIXES.has(w) && !JOIN_PREFIXES.has(word(tokens[i - 1]))) {
      // LEFT [OUTER] JOIN, CROSS JOIN, ...: broken before the first word.
      let j = i + 1;
      while (JOIN_PREFIXES.has(word(tokens[j]))) j += 1;
      return word(tokens[j]) === 'JOIN';
    }
    return false;
  }

  let out = '';
  // One entry per open parenthesis: whether it is a subquery (a new query level).
  const parens: boolean[] = [];
  // Query levels: their depth, and whether their select list is laid out one column per line.
  let level = 0;
  const listOpen: boolean[] = [false];
  // Inside a call or window: the innermost open parenthesis is not a subquery.
  const inCall = () => parens.length > 0 && !parens[parens.length - 1];
  const newline = (depth: number) => {
    out = out.replace(/ +$/, '');
    out += `\n${INDENT.repeat(depth)}`;
  };

  for (let i = 0; i < tokens.length; i += 1) {
    const { text, spaceBefore } = tokens[i];
    const w = text.toUpperCase();
    const atQueryLevel = !inCall();
    let broke = false;

    if (text === ')' && subquery.has(opener.get(i) as number)) {
      level -= 1;
      listOpen.pop();
      newline(level);
      broke = true;
    } else if (atQueryLevel && i > 0 && isClauseAt(i)) {
      // A clause after SELECT ends its list; DISTINCT/ALL after SELECT, UNION are not clauses.
      listOpen[listOpen.length - 1] = false;
      if (!out.endsWith(`\n${INDENT.repeat(level)}`)) newline(level);
      broke = true;
    } else if (atQueryLevel && listOpen[listOpen.length - 1] && tokens[i - 1]?.text === ',') {
      newline(level + 1);
      broke = true;
    } else if (atQueryLevel && i > 0 && tokens[i - 1].text === ';') {
      newline(level);
      broke = true;
    }

    if (!broke && spaceBefore && out.length && !/\n *$/.test(out)) out += ' ';
    out += text;

    if (text === '(') {
      const isSub = subquery.has(i);
      parens.push(isSub);
      if (isSub) {
        level += 1;
        listOpen.push(false);
        newline(level);
      }
    } else if (text === ')') {
      parens.pop();
    } else if (atQueryLevel && w === 'SELECT' && listHasComma(i)) {
      // SELECT [DISTINCT] on its own line, then one column per line.
      listOpen[listOpen.length - 1] = true;
      if (word(tokens[i + 1]) !== 'DISTINCT') newline(level + 1);
    } else if (
      atQueryLevel &&
      w === 'DISTINCT' &&
      word(tokens[i - 1]) === 'SELECT' &&
      listOpen[listOpen.length - 1]
    ) {
      newline(level + 1);
    }
  }
  return out;
}
